import React, { useRef, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';
import { salesService } from '../../services/sales.service';
import { Grid } from '@mui/material';
import GeneralModal from '../../helpers/GeneralModal';
import { createModal } from 'react-modal-promise';
import { SetEditSalesOrder, SetSalesOrderList, GetSalesOrderList } from '../../store/salesOrderAtom';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Dropdown } from 'react-bootstrap';
import LogicContext from '../../Context/LogicContext';
import SalesScroll from './SalesScroll';
import Package from '../Package';
import { SalesCommentWrapper } from './SalesOrderOverviewWrapper';
import SalesOrderHistory from './SalesOrderHistory';
import SalesOrderInvoice from './SalesOrderInvoice';
import { ImAttachment } from 'react-icons/im';
import { FaFilePdf } from 'react-icons/fa';
import { MdDelete, MdEdit } from 'react-icons/md';
import { IoPrintSharp } from 'react-icons/io5';
import { toLowercaseKeys } from '../../helpers/toLowerCaseKeys';
import SalesOrderAddressModal from './SalesOrderAddressModal';
import SalesOrderOverview from './SalesOrderOverview';

const SalesOrderDetailsPage = () => {
  const [tabValue, setTabValue] = useState('1');
  const [selectedList, setSelectedList] = useState([]);
  const myPromiseModal = createModal(GeneralModal);
  const setEditSalesOrder = SetEditSalesOrder();
  const setSalesOrderListData = SetSalesOrderList();
  const salesOrderListData = GetSalesOrderList();
  const logicContext = useContext(LogicContext);
  const { showToast } = useMemo(() => logicContext, [logicContext]); // Memoize context to avoid re-renders
  const { sales_id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [salesData, setSalesData] = useState({});
  const url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];
  const mountRef = useRef(false);
  const printableRef = useRef();


  // Fetch sales order details with abortable request
  useEffect(() => {
    const abortController = new AbortController();
    mountRef.current = true;
    salesService
      .salesOrderDetails(sales_id, { signal: abortController.signal })
      .then(response => {
        const serverData = toLowercaseKeys(response.data);

        if (mountRef.current && response.data !== undefined) {
          setSalesData(serverData || {});
        }
      })
      .catch(err => {
        if (!abortController.signal.aborted) console.error(err);
      });

    return () => {
      mountRef.current = false;
      abortController.abort(); // Clean up pending requests
    };
  }, [sales_id]);

  // Fetch sales order list if empty
  useEffect(() => {
    if (!salesOrderListData?.Sales_Order_Info?.length) {
      const abortController = new AbortController();
      salesService
        .getSalesOrderList({ signal: abortController.signal })
        .then(response => {
          if (response?.data !== undefined) {
            setSalesOrderListData(response.data);
          }
        })
        .catch(err => {
          if (!abortController.signal.aborted) console.error(err);
        });

      return () => abortController.abort();
    }
  }, [salesOrderListData?.Sales_Order_Info?.length, setSalesOrderListData]);

  // Memoized functions to prevent unnecessary re-renders
  const editSalesOrder = useCallback(async () => {
    const response = await myPromiseModal({
      title: 'Modify Sales Order',
      body: 'Do you want to modify this record?',
      showInput: false,
      placeHolder: '',
    });

    if (response.decision === true) {
      setEditSalesOrder(salesData);
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
    }
  }, [currModuleId, currModuleName, currUserMenu, myPromiseModal, navigate, salesData, setEditSalesOrder]);

  const reloadSalesOrderDataData = async () => {
    setSalesData({});
    salesService.salesOrderDetails(sales_id)
      .then(response => {
        const serverData = toLowercaseKeys(response.data);
        if (mountRef.current && serverData !== undefined) {
          if (serverData) {
            setSalesData(serverData);
            setSalesOrderListData(prev => {
              const newList = prev['Sales_Order_Info'].map(salesOrder => {
                return salesOrder?.sales_order_ref_no === serverData.sales_order_ref_no ? response.data : salesOrder;
              });
              return { ...prev, sales_order_info: newList };
            });
          }
        }
      })
      .catch(err => {});
  };

  const viewDetails = useCallback(
    id => {
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${id}`);
    },
    [currModuleId, currModuleName, currUserMenu, navigate]
  );

  const onSelection = useCallback(list => {
    const tempList = list?.filter(item => item.checked === true);
    setSelectedList(tempList);
  }, []);

  const handlePrint = useReactToPrint({
    content: () => printableRef.current,
    documentTitle: `Sales Order - ${salesData?.sales_Order_No || salesData?.sales_order_ref_no || 'Details'}`,
    removeAfterPrint: true,
    bodyClass: 'print-body', // Apply print-specific styles
  });

  const getSalesOrderListApi = useCallback(() => {
    const abortController = new AbortController();
    salesService
      .getSalesOrderInvoiceList({ signal: abortController.signal })
      .then(response => {
        if (response?.data !== undefined) {
          setSalesOrderListData(response.data);
        }
      })
      .catch(err => {
        if (!abortController.signal.aborted) console.error(err);
      });

    return () => abortController.abort();
  }, [setSalesOrderListData]);

  const deleteSalesOrder = useCallback(() => {
    customActionsSingle(4);
  }, []);

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const customTabChange = useCallback(id => {
    setTabValue(id);
  }, []);

  const viewSalesDetails = useCallback(
    salesOrderId => {
      navigate(`/${currModuleId}/${currModuleName}/sales/details/${salesOrderId}`);
    },
    [currModuleId, currModuleName, navigate]
  );

  const createSubscription = useCallback(() => {
    if (salesData?.subscription_ref_no === 0 || salesData?.subscription_ref_no === null) {
      navigate(
        `/${2}/Subscription/subscriptions/add-new?Customer_Id=${salesData?.customer_id}&Customer_Full_Name=${
          salesData?.customer_full_name
        }&sales_order_ref_no=${salesData?.sales_order_ref_no}`
      );
      setEditSalesOrder(salesData);
    } else {
      showToast('Sales order already has a subscription!', 'info');
    }
  }, [navigate, salesData, setEditSalesOrder, showToast]);

  const bulkActions = useCallback(
    async action => {
      const result = await myPromiseModal({
        title: 'Confirm your action!',
        body: `Do you want to ${action.Action_Name}?`,
        showInput: false,
        placeHolder: '',
      });

      if (result.decision === true) {
        let temp = '';
        const option = action.Procedure_Option;
        selectedList.forEach(item => {
          temp += `<a><refno>${item.sales_order_ref_no}</refno></a>`;
        });

        const xml_string_refno = `<root>${temp}</root>`;
        salesService
          .setSalesOrderBulkAction({ xml_string_refno, option })
          .then(res => {
            if (res?.data !== undefined) {
              const { message_text } = res.data[0];
              showToast(message_text, 'success');
              reloadSalesOrderDataData();
            }
          })
          .catch(err => {
            showToast('Error', 'error');
          });
      }
    },
    [myPromiseModal, selectedList, showToast]
  );

  const singleActionPerform = useCallback(
    async action => {
      const result = await myPromiseModal({
        title: 'Confirm your action!',
        body: `This sales status will be ${action.sales_order_status_name}?`,
        showInput: false,
        placeHolder: '',
      });

      if (result.decision === true) {
        const option = action.Procedure_Option;
        const xml_string_refno = `<root><a><refno>${sales_id}</refno></a></root>`;
        salesService
          .setSalesOrderBulkAction({ xml_string_refno, option })
          .then(res => {
            if (res?.data !== undefined) {
              const { message_text } = res.data[0];
              showToast(message_text, 'success');
              reloadSalesOrderDataData();
            }
          })
          .catch(err => {
            showToast('Error', 'error');
          });
      }
    },
    [myPromiseModal, sales_id, showToast]
  );

  const customActionsSingle = useCallback(
    async actionNumber => {
      const action =
        {
          1: { Action_Name: 'Submit for Approval', Action_Id: 1, Procedure_Option: 5 },
          2: { Action_Name: 'Approve', Action_Id: 2, Procedure_Option: 9 },
          10: { Action_Name: 'Convert to Invoice', Action_Id: 10, Procedure_Option: 12 },
          8: { Action_Name: 'Confirm', Action_Id: 8, Procedure_Option: 8 },
          9: { Action_Name: `Reject ${salesData?.sales_order_no}`, Action_Id: 9, Procedure_Option: 13 },
          4: { Action_Name: `Delete ${salesData?.sales_order_no}`, Action_Id: 4, Procedure_Option: 4 },
        }[actionNumber] || {};

      const placeHolder = actionNumber === 9 ? 'reason(s) for rejection' : '';
      const showInput = actionNumber === 9;

      const result = await myPromiseModal({
        title: 'Confirm your action!',
        body: `Do you want to ${action.Action_Name}?`,
        showInput,
        placeHolder,
      });

      if (result.decision === true) {
        const option = action.Procedure_Option;
        const xml_string_refno = `<root><a><refno>${sales_id}</refno></a></root>`;
        salesService
          .setSalesOrderBulkAction({ xml_string_refno, option, order_remark: result.message })
          .then(res => {
            if (res?.data !== undefined) {
              const { message_text } = res.data[0];
              showToast(message_text, 'success');
              if (option === 4) {
                navigate(-1);
                getSalesOrderListApi();
              } else {
                reloadSalesOrderDataData();
              }
            }
          })
          .catch(err => {
            showToast('Error', 'error');
          });
      }
    },
    [myPromiseModal, sales_id, salesData, showToast, navigate, getSalesOrderListApi]
  );

  const magicButtons = useCallback(
    (statusString, statusCode) => {
      switch (statusString) {
        case 'Draft':
          return (
            <button
              onClick={() => customActionsSingle(statusCode)}
              style={{ borderRadius: '5px' }}
              className="px-3 app_blue_color text-white h-[6vh] text-uppercase subheading mr-3"
            >
              Submit for Approval
            </button>
          );
        case 'Pending for Approval':
          return (
            <>
              <button
                onClick={() => customActionsSingle(2)}
                style={{ borderRadius: '5px', backgroundColor: '#476EF8' }}
                className="px-3 app_blue_color text-white h-[6vh] text-uppercase subheading mr-3"
              >
                Approve
              </button>
              <button
                onClick={() => customActionsSingle(9)}
                style={{ borderRadius: '5px' }}
                className="px-3 bg-danger text-white h-[6vh] text-uppercase subheading mr-3"
              >
                Reject
              </button>
            </>
          );
        case 'Approved':
          return (
            <>
              <button
                onClick={() => customActionsSingle(8)}
                style={{ borderRadius: '5px' }}
                className="px-3 h-[6vh] text-uppercase bg-info text-white subheading mr-3"
              >
                Confirm
              </button>
              <button
                onClick={() => customActionsSingle(10)}
                style={{ borderRadius: '5px' }}
                className="px-3 h-[6vh] text-uppercase app_blue_color text-white subheading mr-3"
              >
                Convert to Invoice
              </button>
            </>
          );
        case 'Received & Not Billed':
          return (
            <button
              onClick={() => customActionsSingle(3)}
              style={{ borderRadius: '5px' }}
              className="px-3 app_blue_color text-white h-[6vh] text-uppercase subheading mr-3"
            >
              Convert to Bill
            </button>
          );
        case 'Confirmed':
          return (
            <button
              onClick={createSubscription}
              style={{ borderRadius: '5px' }}
              className="px-3 bg-secondary text-white h-[6vh] text-uppercase subheading mr-3"
            >
              Create Subscription
            </button>
          );
        default:
          return null;
      }
    },
    [createSubscription, customActionsSingle]
  );

  return (
    <section className="mt-2">
      <style>{`
        @media print {
          body.print-body {
            margin: 0;
            padding: 15px;
            font-size: 12px;
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
          }
          .print-header { 
            text-align: center; 
            margin-bottom: 30px; 
            border-bottom: 2px solid #007bff;
            padding-bottom: 20px;
          }
          .print-header h1 {
            color: #007bff;
            margin: 0;
            font-size: 28px;
          }
          button, .btn, .dropdown, .MuiTab-root, .MuiTabList-root {
            display: none !important;
          }
          .MuiTabPanel-root[hidden] {
            display: none !important;
          }
        }
      `}</style>
   

      <section className="w-full">
        <div className="flex flex-row items-center justify-end">
          {magicButtons(salesData?.sales_order_status_name, salesData?.sales_order_status_id)}
          {salesData['Action_Status']?.length && (
            <Dropdown className="mr-3">
              <Dropdown.Toggle className="text-sm text-white bg-[#476EF8]" id="dropdown-basic">
                ACTION
              </Dropdown.Toggle>
              <Dropdown.Menu className="mt-1 shadow-xl border-[1px] border-gray-200 rounded-md bg-white">
                {salesData['Action_Status']?.map((option, i) => (
                  <Dropdown.Item className="border-1 active:bg-gray-200" key={i} onClick={() => singleActionPerform(option)}>
                    <div className="text-sm text-gray-600 py-[2px] hover:bg-100">{option?.sales_order_status_name}</div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
          <div className="flex flex-row items-center space-x-1">
            <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
              <ImAttachment className="h-[20px] w-[20px] flex-shrink-0 text-gray-600" />
            </div>
            <div onClick={handlePrint} className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
              <FaFilePdf className="h-[20px] w-[20px] flex-shrink-0 text-gray-600" />
            </div>
            <div onClick={handlePrint} className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
              <IoPrintSharp className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
            </div>
            <div onClick={editSalesOrder} className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
              <MdEdit className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
            </div>
            <div onClick={deleteSalesOrder} className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
              <MdDelete className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
            </div>
          </div>
        </div>

        <section className="mt-4 lg:flex space-x-1">
          <div className="lg:w-[210px]">
            <SalesScroll viewDetails={viewDetails} onSelection={onSelection} />
          </div>
          <div className="w-full overflow-x-hidden mt-4 lg:mt-0 lg:pl-4" ref={printableRef}>
            <div className="print-header no-print">
              <h1>Sales Order Details</h1>
              <p>Order Reference: {salesData?.sales_order_ref_no || 'N/A'}</p>
            </div>
            <TabContext value={tabValue} className="w-full">
              <Box className="mt-2">
                <TabList onChange={handleTabChange} aria-label="lab API tabs example">
                  <Tab label="Overview" value="1" className="outline-none focus:outline-none" />
                  <Tab label="Comment & History" value="2" className="outline-none focus:outline-none" />
                  <Tab label="Packages" value="3" className="outline-none focus:outline-none" />
                  <Tab label="Invoices" value="4" className="outline-none focus:outline-none" />
                </TabList>
              </Box>
              <TabPanel value="1" className="w-full m-0 p-0">
                <SalesOrderOverview
                  reload={reloadSalesOrderDataData}
                  salesData={salesData}
                  viewSalesDetails={viewSalesDetails}
                  customTabChange={customTabChange}
                />
                {/* <SalesCommentWrapper salesData={salesData} viewSalesDetails={viewSalesDetails} customTabChange={customTabChange} /> */}
              </TabPanel>
              <TabPanel value="2">
                {/* Consider wrapping in Suspense for lazy-loading */}
                <SalesOrderHistory tableData={salesData?.history} />
              </TabPanel>
              <TabPanel value="3">
                {/* Consider wrapping in Suspense for lazy-loading */}
                <Package />
              </TabPanel>
              <TabPanel value="4">
                {/* Consider wrapping in Suspense for lazy-loading */}
                <SalesOrderInvoice tableData={salesData?.invoice_details} />
              </TabPanel>
            </TabContext>
          </div>
        </section>
      </section>
    </section>
  );
};

export default SalesOrderDetailsPage;
